import { useEffect, useState, useRef } from 'react';
import { useIsomorphicLayoutEffect } from '@myrealtrip/web-ui';

import { AUTH_ORIGIN } from '~config/publicEnv';
import { ActionType, useAuthContext, useAuthDispatchContext } from '~contexts/AuthProvider';
import { useWebView } from '@myrealtrip/device-detect';
import { useMember } from '~queries/member/useMember';
import useTokenQueryForWeb from '~queries/token/useTokenQueryForWeb';
import useTokenQueryForWebView from '~queries/token/useTokenQueryForWebView';
import { IS_SSR } from '~constants/common';

interface Options {
  required?: boolean;
  redirect?: string;
  onUnauthenticated?: () => void;
}

type Status = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * 인증 제어 훅
 *
 * @param options - required, redirect, onUnauthenticated 값을 설정한다.
 * @param options.required 리다이렉트 필요 여부
 * @param options.redirect 커스텀 리다이렉트 URL
 * @param onUnauthenticated 인증 실패 시 실행할 콜백
 * @return status 인증 처리 상태, 'loading' | 'authenticated' | 'unauthenticated'
 * @return isLoggedIn 로그인 여부
 * @return user 유저 정보
 * @example
 * ```ts
 *   function PrivateComponent() {
 *     const { status } = useAuth({ required: true, redirect: '/custom/page', onUnauthenticated: () => console.log('인증 실패')  })
 *   }
 *   if (status === 'loading) return <Loading />
 *   if (status === 'unauthenticated) return <AccessDenined /> // or !isLoggedIn
 *   return (...)
 * ```
 *  * @example
 * ```ts
 *   function GetUserIdComponent() {
 *     const { status, user } = useAuth()
 *   }
 *   useEffect(() => if (status === 'authenticated') sendLog({ userId: user?.id }), [status]);
 * ```
 */

export default function useAuth(options?: Options) {
  const { isLoggedIn } = useAuthContext();
  const { isWebView } = useWebView();
  const authDispatch = useAuthDispatchContext();
  const [status, setStatus] = useState<Status>('loading');
  const { data: userData } = useMember();

  const {
    isSuccess: isWebSuccess,
    isError: isWebError,
    data: webData,
  } = useTokenQueryForWeb({ enabled: isLoggedIn && !isWebView });
  const {
    isSuccess: isWebViewSuccess,
    isError: isWebViewError,
    data: webViewData,
  } = useTokenQueryForWebView({
    enabled: isLoggedIn && isWebView,
  });

  const isSuccess = isWebSuccess || isWebViewSuccess;
  const isError = isWebError || isWebViewError;
  const data = webData ?? webViewData ?? null;

  const { required, redirect, onUnauthenticated } = options ?? {};

  const callbacks = useRef({ onUnauthenticated });

  useIsomorphicLayoutEffect(() => {
    callbacks.current = { onUnauthenticated };
  });

  useEffect(() => {
    if (isSuccess && data?.data?.apiAccessToken) {
      setStatus('authenticated');
      authDispatch({
        type: ActionType.UPDATE,
        payload: { token: data.data?.apiAccessToken ?? null },
      });
    }
  }, [authDispatch, data, isSuccess]);

  useEffect(() => {
    if (isError) {
      setStatus('unauthenticated');
      authDispatch({ type: ActionType.LOGOUT });
    }
  }, [authDispatch, isError]);

  useEffect(() => {
    if ((status === 'unauthenticated' && isError) || (isSuccess && !data?.data?.apiAccessToken)) {
      callbacks.current.onUnauthenticated?.();

      if (required) {
        const encodedRedirectUrl = IS_SSR ? '' : encodeURIComponent(window.location.href);
        const signInUrl = isWebView
          ? `mrt://signin?return_to=${encodedRedirectUrl}`
          : `${AUTH_ORIGIN}/users/sign_in`;

        window.location.href = redirect ?? signInUrl;
      }
    }
  }, [isError, isSuccess, isWebView, onUnauthenticated, redirect, required, status, data]);

  return {
    status,
    isLoggedIn,
    user: userData,
  };
}
